import React from 'react';
import config from '../../config/GraphViewConfig';
import EmployeesIcon from "../../../../../../../../assets/icons/nav/EmployeesIcon";
import {truncateTextToFit} from "../../../../util";
import {useGraphRelationContext} from "../../services/ButtonRelationService";

/*
 * A node that hands the chat to an operator.
 *
 * Drawn apart from the ordinary node rather than as a variation of it, because
 * almost nothing it has applies here: no buttons, no plus to add more, no row of
 * action flags, no attachments, no list of messages. What is left is a small
 * card saying where the branch ends - and small is the point. Reading a tree,
 * you should be able to see at a glance which paths finish at a person, and a
 * full-size card covered in controls that do nothing says the opposite.
 *
 * It keeps the port at the top, because edges still arrive here. It has nothing
 * at the bottom, because nothing leaves.
 */
const TransferNode = ({node, isHighlighted, onSelect, onNodeContextMenu, connectButtonToDestination}) => {

    const {activeButtonId, setActiveButtonId, setStartPoint, setEndPoint} = useGraphRelationContext();

    const nodeConfig = config.design.node;
    const size = nodeConfig.transfer;

    const top = -node.height / 2;

    const portCx = 0;
    const portCy = top + nodeConfig.port.height / 2;

    const bodyY = top + nodeConfig.port.height + nodeConfig.nodeAndNodePortGap;
    const bodyX = -size.width / 2;

    const title = node.data.name ? node.data.name : String(node.id);

    /* the first line of what the bot says before handing over - more use here
       than a label repeating the node type, which the shape already says */
    const firstLine = (node.data.message || '').split('/n')[0].trim();

    return (
        <g
            className={'tree-node-container transfer ' + (node.isFree ? 'free' : '')}
            data-node-id={node.id}
            transform={`translate(${node.x + node.width / 2},${node.y + node.height / 2})`}
        >
            {/* the same inlet the ordinary node has - an edge arriving
                should not have to be read differently here */}
            <circle
                id={`node-port-${node.id}`}
                cx={portCx}
                cy={portCy}
                r={nodeConfig.port.width / 2}
                className={'tree-node-port' + (isHighlighted ? ' highlighted' : '')}
            />

            <g
                className="tree-node-body"
                onClick={() => onSelect([])}
                onContextMenu={e => onNodeContextMenu(e, node)}
                onMouseUp={(event) => {
                    if (!activeButtonId) return;

                    event.stopPropagation();

                    /*
                     * A button may lead here like it leads anywhere else. This
                     * cleared the drag and forgot to make the connection, so
                     * dropping a button on one of these looked like the node had
                     * refused it - the line simply vanished.
                     */
                    connectButtonToDestination(activeButtonId, node.id);

                    setActiveButtonId(null);
                    setStartPoint(null);
                    setEndPoint(null);
                }}
            >
                {/* a pill rather than a rounded rectangle: the shape alone tells
                    the two kinds of node apart when the canvas is zoomed out far
                    enough that no text is readable */}
                <rect
                    x={bodyX}
                    y={bodyY}
                    width={size.width}
                    height={size.height}
                    rx={size.height / 2}
                    ry={size.height / 2}
                    strokeWidth={2}
                    filter="url(#nodeShadow)"
                    className="transfer-node__rect"
                />

                {/*
                  * Bigger than the 18px it was. On a solid pill this mark is the
                  * only thing that says what kind of node this is once the
                  * canvas is zoomed out far enough that the text has gone, and a
                  * filled icon of several small figures needs the room to stay
                  * legible at all.
                  */}
                <g transform={`translate(${bodyX + 14}, ${bodyY + size.height / 2 - 11})`}>
                    <EmployeesIcon width={22} height={22} style={{color: '#fff'}}/>
                </g>

                <text
                    x={bodyX + 44}
                    y={bodyY + size.height / 2 - 2}
                    textAnchor="start"
                    fontSize={13}
                    fontWeight={600}
                    fill="#fff"
                    pointerEvents="none"
                >
                    {truncateTextToFit(title, size.width - 56, 13)}
                </text>

                <text
                    x={bodyX + 44}
                    y={bodyY + size.height / 2 + 12}
                    textAnchor="start"
                    fontSize={11}
                    fill="rgba(255,255,255,0.75)"
                    pointerEvents="none"
                >
                    {truncateTextToFit(firstLine, size.width - 56, 11)}
                </text>
            </g>
        </g>
    );
};

export default TransferNode;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/GraphView/components/Node/TransferNode.jsx